import type { CabinetEventKind, CabinetEventView } from "@planerius/contracts/cabinet-events"

import { formatTimeOfDay, shiftDate, todayInZone } from "../../lib/format"
import { zoneCity } from "../../shell/timezone-note"

/**
 * Центр уведомлений 1-Э1.9 — слова и правила показа, отдельно от разметки.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. Главные решения этого экрана словесные и проверяемые: как
 * называется вид события, какая кнопка стоит у строки, в какую группу строка попадает
 * и что написано, когда ленты нет вовсе. Проверить их тестом на функции можно,
 * на разметке — только глазами, а глаза на приёмке смотрят в другое место.
 *
 * ТЕКСТ СОБЫТИЯ ЗДЕСЬ НЕ СОБИРАЕТСЯ. Он приходит с сервера готовым и снимком
 * (apps/api/.../events-text.ts): «Иванова Мария, сегодня, 15:00» описывает то, что было
 * в момент события, и пересобранный на чтении текст перестал бы объяснять, что случилось
 * в тот день. Кабинету принадлежат подписи ВОКРУГ текста — вид, кнопка, группа.
 */

/** Подпись вида события — левый ярлык строки, как в макете. */
const KIND_WORDS: Readonly<Record<CabinetEventKind, string>> = {
  "appointment.online_pending": "Новая запись онлайн",
  "appointment.unassigned": "Без мастера",
  "appointment.cancelled_by_client": "Отмена клиентом",
  "appointment.confirmed_by_client": "Подтверждение",
  "appointment.no_show": "Неявка",
  "master.appointment_cancelled": "Отмена",
  "master.appointment_moved": "Перенос",
  "master.schedule_changed": "График",
  "master.timeoff_requested": "Отсутствие",
}

export function kindWords(kind: CabinetEventKind): string {
  return KIND_WORDS[kind] ?? "Событие"
}

/**
 * ЧИПЫ ФИЛЬТРОВ.
 *
 * Первые два — состояние ленты («требуют действия» против «всё»), остальные — вид события.
 * Порядок из макета и не перестраивается по частоте: человек находит фильтр там, где
 * нашёл его в прошлый раз.
 *
 * ЧИПА «КАНАЛЫ» ИЗ МАКЕТА ЗДЕСЬ НЕТ. За ней стоял бы единственный вид события — «бот
 * салона не отвечает», а у него нет источника: ни таблицы ботов, ни шлюза в продукте
 * пока не существует. Чип, который никогда ничего не находит, человек нажимает один раз
 * и делает вывод, что лента сломана.
 */
export interface EventFilter {
  readonly id: "action" | "all" | "appointments" | "cancels" | "schedule"
  readonly label: string
  /** Пусто — фильтр не по виду, а по состоянию. */
  readonly kinds?: readonly CabinetEventKind[]
  /** Показывать ли счётчик рядом с подписью (у состояний он есть, у видов — нет). */
  readonly counted: boolean
}

export const EVENT_FILTERS: readonly EventFilter[] = [
  { id: "action", label: "Требуют действия", counted: true },
  { id: "all", label: "Все", counted: true },
  {
    id: "appointments",
    label: "Записи",
    counted: false,
    kinds: [
      "appointment.online_pending",
      "appointment.unassigned",
      "appointment.confirmed_by_client",
      "master.appointment_moved",
    ],
  },
  {
    id: "cancels",
    label: "Отмены",
    counted: false,
    kinds: ["appointment.cancelled_by_client", "appointment.no_show", "master.appointment_cancelled"],
  },
  { id: "schedule", label: "График", counted: false, kinds: ["master.schedule_changed", "master.timeoff_requested"] },
]

export function filterById(id: string): EventFilter {
  return EVENT_FILTERS.find((f) => f.id === id) ?? EVENT_FILTERS[0]!
}

/**
 * ЧТО МОЖНО СДЕЛАТЬ СО СТРОКОЙ.
 *
 * `work` — главная кнопка: она ДЕЛАЕТ дело, после которого событие закрывается само
 * (подтверждение записи) или уводит туда, где дело делается (назначение мастера в журнале).
 * `link` — просто «посмотреть», у информационных строк.
 *
 * ПОЧЕМУ У «НАЗНАЧИТЬ» НЕ ЗАКРЫВАЕТСЯ СОБЫТИЕ ПО НАЖАТИЮ. Нажатие только открывает
 * журнал; мастера человек назначит через несколько секунд — или не назначит, передумав.
 * Закрыть дело в момент перехода значило бы стереть напоминание о работе, которая ещё
 * не сделана. Закрывает его сервер, в тот момент, когда исполнитель действительно
 * появился (cabinet-appointments.service.ts).
 */
export type EventAction =
  | { readonly kind: "confirm"; readonly label: string; readonly appointmentId: string }
  | { readonly kind: "open-journal"; readonly label: string; readonly appointmentId: string }
  | { readonly kind: "open-visit"; readonly label: string; readonly appointmentId: string }
  | { readonly kind: "open-week"; readonly label: string }
  | {
      readonly kind: "open-leave"
      readonly label: string
      readonly employeeId: string
      readonly from: string
      readonly to: string
    }

export interface EventRow {
  readonly event: CabinetEventView
  readonly typeLabel: string
  /** Требует действия и ещё не потеряло смысл. */
  readonly needsWork: boolean
  /** Главная кнопка: только у строк, требующих действия. */
  readonly work: EventAction | null
  /** «Посмотреть»: у остальных строк, если смотреть есть куда. */
  readonly link: EventAction | null
}

/**
 * Разбор строки: вид, кнопки и состояние.
 *
 * `master` — лента мастера (адреса /my/*) или администратора: одно и то же событие ведёт
 * их в РАЗНЫЕ места. Мастеру журнал салона закрыт, и «Открыть запись» для него — это его
 * собственная карточка визита; администратору — журнал с открытой панелью записи.
 */
export function rowOf(event: CabinetEventView, master: boolean): EventRow {
  const appointmentId = event.target?.type === "appointment" ? event.target.id : null
  const stale = event.staleNote !== null
  const needsWork = event.requiresAction && !stale && !event.closed

  let work: EventAction | null = null
  let link: EventAction | null = null

  if (needsWork && event.kind === "master.timeoff_requested" && event.target?.type === "schedule" && event.period !== null && !master) {
    // Просьба мастера об отсутствии (5-М6): форма оформления — уже с его датами.
    work = {
      kind: "open-leave",
      label: "Оформить отсутствие",
      employeeId: event.target.id,
      from: event.period.from,
      to: event.period.to,
    }
  } else if (needsWork && appointmentId !== null) {
    if (event.kind === "appointment.online_pending") {
      work = { kind: "confirm", label: "Подтвердить", appointmentId }
    } else if (event.kind === "appointment.unassigned") {
      work = { kind: "open-journal", label: "Назначить", appointmentId }
    } else if (master) {
      work = { kind: "open-visit", label: "Открыть запись", appointmentId }
    } else {
      work = { kind: "open-journal", label: "Открыть запись", appointmentId }
    }
  } else if (appointmentId !== null && !stale) {
    link = master
      ? { kind: "open-visit", label: "Открыть запись", appointmentId }
      : { kind: "open-journal", label: "Открыть запись", appointmentId }
  } else if (event.target?.type === "schedule" && master) {
    // Своё расписание мастер смотрит на «Неделе»; администратору эта строка не адресована
    // вовсе — она уехала мастеру, — поэтому ссылки для него здесь нет.
    link = { kind: "open-week", label: "Открыть неделю" }
  }

  return { event, typeLabel: kindWords(event.kind), needsWork, work, link }
}

/**
 * Подпись второй кнопки.
 *
 * У ДЕЛА — «РАЗОБРАНО», У ФАКТА — «ПРОЧИТАНО», и это разные действия, а не два слова
 * для одного. «Разобрано» гасит дело и ставит под ним подпись человека: он отвечает
 * за то, что работа сделана. «Прочитано» только снимает выделение со строки и счётчик
 * дел не трогает — иначе кнопка стала бы способом обнулить счётчик, ничего не сделав.
 *
 * В макете у обеих строк написано «Прочитано» и «Скрыть». «Скрыть» обещает, что строка
 * исчезнет, — она остаётся в общей ленте; отступление записано в ВОПРОСАХ-ДИЗАЙНЕРУ.
 */
export function secondaryLabel(row: EventRow): string {
  return row.needsWork ? "Разобрано" : "Прочитано"
}

/** Группа ленты: «Сегодня · 6». */
export interface EventGroup {
  readonly key: string
  readonly title: string
  readonly rows: readonly EventRow[]
}

const MONTHS = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
] as const

/**
 * ДЕНЬ И ВРЕМЯ ЛЕНТЫ — ПО ЧАСАМ ФИЛИАЛА, А НЕ УСТРОЙСТВА.
 *
 * Здесь стояли getDate()/getHours() — пояс браузера. Владелец в Москве открывал ленту
 * точки во Владивостоке и читал «клиент отменил в 02:40» вместо 09:40, а строки одного
 * рабочего дня филиала расползались по группам «Сегодня» и «Вчера» — при том, что текст
 * самой строки сервер собрал в поясе филиала, и заголовок со строкой говорили о разных
 * сутках (разбор wwtalq62e, находки 10 и 16). Это ровно та авария, от которой кабинет
 * защищается полосой пояса: «16:40», прочитанное как своё время.
 */
function dayKey(value: Date, timeZone: string): string {
  return todayInZone(timeZone, value)
}

/**
 * Заголовок группы.
 *
 * «СЕГОДНЯ» И «ВЧЕРА» СЛОВАМИ, ДАЛЬШЕ ДАТОЙ. Администратор разбирает ленту глазами
 * и решает, что срочно: «сегодня» требует внимания сейчас, «14 сентября» — нет. Дата
 * вместо слова заставляет считать дни в голове, и он ошибается там, где дороже всего.
 *
 * Счёт в заголовке — из макета («Сегодня · 48»): по нему видно, что лента длинная,
 * до того, как человек начал её прокручивать.
 */
export function groupTitle(key: string, now: Date, count: number, timeZone: string): string {
  const today = dayKey(now, timeZone)
  // «Вчера» — предыдущая КАЛЕНДАРНАЯ дата филиала, а не «минус 24 часа»: в ночь перевода
  // часов сутки длятся 23 или 25 часов, и вычитание суток промахивается мимо вчерашнего дня.
  const yesterday = shiftDate(today, -1)
  const suffix = ` · ${count}`
  if (key === today) return `Сегодня${suffix}`
  if (key === yesterday) return `Вчера${suffix}`
  const month = MONTHS[Number(key.slice(5, 7)) - 1]
  const day = Number(key.slice(8, 10))
  return month === undefined ? `${key}${suffix}` : `${day} ${month}${suffix}`
}

/**
 * Лента по дням, в порядке сервера.
 *
 * Порядок строк НЕ пересортировывается: он пришёл с курсором, и своя сортировка
 * на клиенте рассыпала бы вторую страницу — курсор считает по тому же порядку.
 */
export function groupRows(
  events: readonly CabinetEventView[],
  now: Date,
  master: boolean,
  /** Пояс строки — филиала самого события (у ленты одного филиала он у всех один). */
  zoneOf: (event: CabinetEventView) => string,
): readonly EventGroup[] {
  /*
   * ДЕНЬ СТРОКИ — В ПОЯСЕ ЕЁ ФИЛИАЛА, И ЗАГОЛОВОК ГРУППЫ — В ТОМ ЖЕ ПОЯСЕ.
   *
   * В ленте сети («все филиалы») и у мастера строки разных поясов стоят рядом. Если день
   * группы считать в одном поясе, а время строки — в другом, выходит «Вчера · 16:00» —
   * момент, которого не было ни в одном из поясов, а дело «записался на 19:00, сегодня»
   * под «Вчера» читается просроченным (разбор w2but9x36, находка 20). Поэтому, когда поясов
   * в ленте несколько, группа — это день ОДНОГО пояса, и в заголовке назван его город.
   *
   * Ключ группы единственный: строки сервера идут по моменту, и дни разных поясов могут
   * чередоваться — повтор ключа сломал бы список. Порядок групп — по первой строке.
   */
  const zones = new Set(events.map(zoneOf))
  const several = zones.size > 1
  const byKey = new Map<string, { day: string; zone: string; rows: EventRow[] }>()
  for (const event of events) {
    const zone = zoneOf(event)
    const day = dayKey(new Date(event.createdAt), zone)
    const key = several ? `${day}@${zone}` : day
    const group = byKey.get(key) ?? { day, zone, rows: [] }
    if (!byKey.has(key)) byKey.set(key, group)
    group.rows.push(rowOf(event, master))
  }
  return [...byKey.entries()].map(([key, group]) => {
    const title = groupTitle(group.day, now, group.rows.length, group.zone)
    const city = several ? zoneCity(group.zone) ?? group.zone : null
    return { key, title: city === null ? title : `${title} · ${city}`, rows: group.rows }
  })
}

/**
 * Время строки: день уже назван заголовком группы, поэтому здесь только часы.
 *
 * Пояс — ФИЛИАЛА САМОГО СОБЫТИЯ: в ленте сети строки разных точек стоят рядом, и время
 * обязано совпадать с часами в тексте строки, который сервер собрал в поясе её филиала.
 */
export function rowTime(createdAt: string, timeZone: string): string {
  const value = new Date(createdAt)
  if (Number.isNaN(value.getTime())) return ""
  return formatTimeOfDay(value, timeZone)
}

/**
 * Пустая лента: ТРИ РАЗНЫЕ новости, и путать их нельзя.
 *
 * «Всё разобрано» — похвала: дел нет, но события были. «События появятся, когда клиенты
 * начнут записываться» — первый день салона: закрывать нечего и волноваться не о чем.
 * А пустота под чипом вида — это не пустая лента, а выбранный фильтр, и сказать об этом
 * обязательно: иначе человек решит, что центр не работает.
 */
export function emptyWords(filter: EventFilter): { readonly title: string; readonly text: string } {
  if (filter.id === "action") {
    return { title: "Всё разобрано", text: "Новые события появятся здесь." }
  }
  if (filter.id === "all") {
    return {
      title: "События появятся, когда клиенты начнут записываться",
      text: "Закрывать здесь пока нечего.",
    }
  }
  return {
    title: `В фильтре «${filter.label}» событий нет`,
    text: "Снимите фильтр, чтобы увидеть остальные события.",
  }
}

/**
 * Подпись под лентой мастера.
 *
 * В макете здесь «Пуши работают в приложении. Пока уведомления приходят в бот» — и это
 * неправда: персоналу не доставляется ничего, ни бота, ни пушей в продукте нет. Обещание
 * доставки страшнее молчания: мастер, поверивший, что об отмене ему сообщат, перестанет
 * открывать ленту — и узнает об отмене, приехав на работу.
 */
export const MASTER_FOOTER =
  "Уведомления приходят сюда. Внешней доставки — в бот, на почту, пушем — пока нет: " +
  "заглядывайте в ленту сами."

/** Информационные строки страницы: их и гасит «Прочитать все». */
export function unreadInformational(rows: readonly EventRow[]): readonly string[] {
  return rows.filter((row) => !row.needsWork && !row.event.read).map((row) => row.event.id)
}
